import { apiClient } from "../config/apiClient";
import { internalApiClient } from "../config/internalApiClient";
import { mapProductOption } from "../lib/product";
import type {
  ApiProduct,
  MarketPriceApiResponse,
  ProductListResponse,
  ProductOption,
  ProductOptionSource,
  UserDetails,
} from "../types/domain";

const getProducts = async (): Promise<ApiProduct[] | null> => {
  try {
    const response = await apiClient.get<ApiProduct[] | { products?: ApiProduct[] }>("products");
    const data = response?.data ?? response;
    if (Array.isArray(data)) {
      return data;
    }
    if (Array.isArray((data as { products?: ApiProduct[] }).products)) {
      return (data as { products?: ApiProduct[] }).products ?? [];
    }
    return null;
  } catch (e) {
    console.log("getProducts error:", e);
    return null;
  }
};

const getProductById = async (productId: string): Promise<ApiProduct | null> => {
  try {
    const response = await apiClient.get<ApiProduct>(`products/${productId}`);
    return response?.data ?? response;
  } catch (e) {
    console.log("getProductById error:", e);
    return null;
  }
};

const getMarketPrices = async (): Promise<MarketPriceApiResponse | null> => {
  try {
    const response = await apiClient.get<MarketPriceApiResponse>("market-prices");
    return response?.data ?? response;
  } catch (e) {
    console.log("getMarketPrices error:", e);
    return null;
  }
};

const getProductsByType = async (type: "buy" | "sell" | "exchange"): Promise<ProductOption[]> => {
  let apiType = "";
  if (type === "buy") apiType = "Buy";
  else if (type === "sell") apiType = "Sell";
  else if (type === "exchange") apiType = "Exchange";

  try {
    const response = await apiClient.get<ProductListResponse | ProductOptionSource[]>(
      `products/list?type=${apiType}`,
      {
        headers: { type: apiType },
      }
    );
    const data = response?.data ?? response;
    if (Array.isArray((data as ProductListResponse).products)) {
      return ((data as ProductListResponse).products ?? []).map(mapProductOption);
    }

    if (Array.isArray(data)) {
      return (data as ProductOptionSource[]).map(mapProductOption);
    }

    return [];
  } catch (e) {
    console.log("getProductsByType error:", e);
    return [];
  }
};

const getUserDetails = async (userId: string): Promise<UserDetails> => {
  try {
    const response = await internalApiClient.get<UserDetails>(`/api/user-details?userId=${userId}`);
    return response?.data ?? response;
  } catch (e) {
    console.log("getUserDetails error:", e);
    throw e;
  }
};

export type CreateExchangePayload = {
  productToGiveId: string;
  quantityToGive: number;
  productToReceiveId: string;
  quantityToReceive: number;
  operation: "EXCHANGE";
  fullName: string;
  gstNumber?: string;
  shippingAddress: string;
  city: string;
  pincode: string;
  amount?: number;
};

const createExchange = async (payload: CreateExchangePayload): Promise<unknown> => {
  try {
    const response = await internalApiClient.post("/api/exchange", payload, {
      toast: {
        successMessage: "Exchange request submitted successfully.",
        errorMessage: "Failed to submit exchange request. Please try again.",
      },
    });
    return response?.data ?? response;
  } catch (e) {
    console.log("createExchange error:", e);
    throw e;
  }
};

export type CreateBuyPayload = {
  productId: string;
  quantity: number;
  amount: number;
  operation: "BUY";
  fullName: string;
  gstNumber?: string;
  address: string;
  pincode: string;
};

const createBuy = async (payload: CreateBuyPayload): Promise<unknown> => {
  try {
    const response = await internalApiClient.post("/api/buy", payload, {
      toast: {
        successMessage: "Buy request submitted successfully.",
        errorMessage: "Failed to submit purchase request. Please try again.",
      },
    });
    return response?.data ?? response;
  } catch (e) {
    console.log("createBuy error:", e);
    throw e;
  }
};

export type CreateSamplePayload = {
  productId: string;
  exchangeProductId?: string;
  operation: "BUY" | "SELL" | "EXCHANGE";
  recipientName: string;
  streetAddress: string;
  apartment?: string;
  city: string;
  state: string;
  pincode: string;
  phoneNumber: string;
  senderName: string;
  fullName?: string;
};

const createSample = async (payload: CreateSamplePayload): Promise<unknown> => {
  try {
    const response = await internalApiClient.post("/api/sample", payload, {
      toast: {
        successMessage: "Sample request submitted successfully.",
        errorMessage: "Failed to request sample. Please try again.",
      },
    });
    return response?.data ?? response;
  } catch (e) {
    console.log("createSample error:", e);
    throw e;
  }
};

export const userService = {
  getProducts,
  getProductById,
  getMarketPrices,
  getProductsByType,
  createExchange,
  createBuy,
  createSample,
  getUserDetails,
};
